import React, { useState } from 'react';
import CircularProgress from '../CircularProgress/CircularProgress';
// @ts-ignore
import checkIcon from '../../image-assets/icon.checkmark.svg';
// @ts-ignore
import addIcon from '../../image-assets/icon.add.svg';
import LanguageDialog from '../LanguageDialog/LanguageDialog';
import './LanguageTabs.css';

interface LanguageTranslationStat {
  language: string;
  languageName: string;
  missingCount: number;
  totalKeys: number;
  canTranslate: boolean;
  isLoading?: boolean;
  progress?: {
    completed_items: number;
    total_items: number;
    progress_percentage: number;
  };
  completionState?: {
    isComplete: boolean;
    translatedKeys: number;
    missingKeys: number;
    totalKeys: number;
    lastVerified: string;
  };
}

interface LanguageTabsProps {
  languageStats: LanguageTranslationStat[];
  sourceLanguage?: string;
  sourceLanguageName?: string;
  sourceMissingCount?: number;
  selectedLanguage?: string;
  onLanguageSelect: (language: string) => void;
  onDeleteLanguageTranslations?: (language: string) => void;
  activeJobs: Map<string, { jobId: string; progress: any }>;
  isAdmin?: boolean;
  canEditLanguages?: boolean;
  onAddLanguage?: (language: string) => void;
  existingLanguages?: string[];
}

const LanguageTabs: React.FC<LanguageTabsProps> = React.memo(({
  languageStats,
  sourceLanguage,
  sourceLanguageName,
  sourceMissingCount = 0,
  selectedLanguage,
  onLanguageSelect,
  onDeleteLanguageTranslations,
  activeJobs,
  isAdmin = false,
  canEditLanguages = false,
  onAddLanguage,
  existingLanguages = []
}) => {
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);
  

  
  // Use the languageStats in their original order, marking the source language
  const allLanguages = languageStats.map(stat => ({
    ...stat,
    isSource: stat.language === sourceLanguage
  }));

  // Show empty state only when there are truly no languages
  if (allLanguages.length === 0) {
    return (
      <div className="view-container">
        <div className="content">
          <div className="language-tabs">
            <div className="empty-state">
              <p>No languages available for translation</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for UI
  const allComplete = languageStats.every(stat => stat.missingCount === 0);

  const handleAddLanguage = (language: string) => {
    if (onAddLanguage) {
      onAddLanguage(language);
      // Navigate to the newly added language
      onLanguageSelect(language);
    }
    setIsLanguageDialogOpen(false);
  };

  return (
    <>
      <div className="language-tabs">
          <div className="language-tabs-container">
            {allLanguages.map((stat) => {
              const activeJob = activeJobs.get(stat.language);
              const isLoading = !!activeJob;
              
              // Use active job progress for real-time accuracy
              const progress = activeJob?.progress;
              const isComplete = stat.completionState?.isComplete || stat.missingCount === 0;
              const isSource = (stat as any).isSource;
              const isSelected = selectedLanguage === stat.language;



              return (
                <div 
                  key={stat.language} 
                  className={`language-tab ${isSelected ? 'active' : ''} ${isSource ? 'source' : ''} ${isLoading ? 'loading' : ''} ${isComplete && !isSource ? 'complete' : ''}`}
                  onClick={() => onLanguageSelect(stat.language)}
                >
                  <div className="tab-left">
                    <div className="language-header">
                      <span className="language-name">{stat.languageName}</span>
                      <span className="language-code">{stat.language.toUpperCase()}</span>
                    </div>
                    <div className="tab-status">
                      {isLoading && progress ? (
                        <div className="translation-progress">
                          <CircularProgress
                            progress={progress?.total_items && progress.total_items > 0 ? Math.round(((progress?.completed_items || 0) / progress.total_items) * 100) : 0}
                            size={14}
                            strokeWidth={2}
                            color="var(--text-primary)"
                            backgroundColor="var(--divider)"
                            className="translation-progress-circle"
                            animated={true}
                          />
                          <div className="progress-text">
                            Translating {progress.completed_items} of {progress.total_items}
                          </div>
                        </div>
                      ) : isSource ? (
                        <div className="status-text">
                          {stat.missingCount > 0 ? `${stat.missingCount} missing` : 'Complete'}
                        </div>
                      ) : isComplete ? (
                        <div className="status-text complete">
                          <img src={checkIcon} alt="Complete" className="completion-icon" />
                          Complete
                        </div>
                      ) : (
                        <div className="status-text missing">
                          {stat.completionState ? 
                            `${Math.max(0, stat.completionState.missingKeys)} missing` : 
                            `${Math.max(0, stat.missingCount)} missing`
                          }
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="tab-right">
                    {isSource ? (
                      <span className="source-label">Source</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
            
            {/* Add Language Tab - Only visible to admins */}
            {canEditLanguages && onAddLanguage && (
              <div 
                className="language-tab add-language-tab"
                onClick={() => setIsLanguageDialogOpen(true)}
              >
                <div className="add-language-content">
                  <img src={addIcon} alt="Add" className="add-icon" />
                  <span className="add-language-text">Add Language</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Language Dialog for adding new languages */}
        {canEditLanguages && onAddLanguage && (
          <LanguageDialog
            isOpen={isLanguageDialogOpen}
            onClose={() => setIsLanguageDialogOpen(false)}
            onSelect={handleAddLanguage}
            existingLanguages={existingLanguages}
            onAdd={handleAddLanguage}
            isLoading={false}
          />
        )}
      </>
    );
}, (prevProps, nextProps) => {
  // Custom comparison function for LanguageTabs memoization
  return (
    prevProps.selectedLanguage === nextProps.selectedLanguage &&
    prevProps.languageStats === nextProps.languageStats &&
    prevProps.activeJobs === nextProps.activeJobs &&
    prevProps.sourceLanguage === nextProps.sourceLanguage &&
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.canEditLanguages === nextProps.canEditLanguages
  );
});

export default LanguageTabs;
